import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Cloud, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw, Power, BookOpen, Tag, Save } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const [drive, setDrive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/drive/status");
      setDrive(data);
    } catch {
      setDrive({ configured: false });
    }
  };
  useEffect(() => {
    load();
    if (window.location.search.includes("drive=connected")) {
      window.history.replaceState({}, "", "/settings");
    } else if (window.location.search.includes("drive=error")) {
      const params = new URLSearchParams(window.location.search);
      alert("Google Drive authorisation failed: " + (params.get("msg") || "unknown error"));
      window.history.replaceState({}, "", "/settings");
    } else if (window.location.search.includes("zoho=connected")) {
      window.history.replaceState({}, "", "/settings");
    } else if (window.location.search.includes("zoho=error")) {
      const params = new URLSearchParams(window.location.search);
      alert("Zoho Books authorisation failed: " + (params.get("msg") || "unknown error"));
      window.history.replaceState({}, "", "/settings");
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/drive/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Drive?")) return;
    await api.post("/drive/disconnect");
    await load();
  };

  const pollNow = async () => {
    setBusy(true);
    setPollResult(null);
    try {
      const { data } = await api.post("/drive/poll-now");
      setPollResult(data);
      // The poll runs in the background. Poll status every 5s for up to 30 min.
      let attempts = 0;
      const tick = async () => {
        attempts += 1;
        try {
          const { data: s } = await api.get("/drive/status");
          setDrive(s);
          if (!s.poll_running || attempts > 360) {
            await load();
            return;
          }
          setTimeout(tick, 5000);
        } catch (_) {
          // Network blip — try again
          if (attempts <= 360) setTimeout(tick, 5000);
        }
      };
      setTimeout(tick, 4000);
    } catch (e) {
      setPollResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const isOwner = user?.role === "Owner";

  return (
    <div data-testid="settings-page">
      <PageHeader
        eyebrow="Admin"
        title="Integrations"
        description="Integrations and system configuration."
      />

      <section className="bg-card border border-border rounded p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="label-tiny mb-1">Integration</p>
            <h2 className="font-display text-2xl font-bold flex items-center gap-2">
              <Cloud className="w-5 h-5 text-primary" />
              Google Drive — daily ProRoast sync
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Drop your daily ProRoast .sqlite backup into a Google Drive folder. The app syncs <strong>once every night at 23:00 SAST</strong> —
              picking up new files and ingesting them automatically. You can also trigger a manual sync any time below.
            </p>
          </div>
        </div>

        {!drive ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : !drive.configured ? (
          <div className="bg-secondary border border-border rounded p-4 text-sm" data-testid="drive-not-configured">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[hsl(31,65%,53%)] mt-0.5" />
              <p className="font-semibold">Drive integration is not yet configured.</p>
            </div>
            <p className="text-muted-foreground mb-3">
              Setup is a one-time, ~5-minute task in Google Cloud Console:
            </p>
            <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
              <li>Open <a className="text-primary underline" target="_blank" rel="noopener" href="https://console.cloud.google.com">console.cloud.google.com</a> and create a project (e.g. "Brothers Coffee").</li>
              <li>Enable the <strong>Google Drive API</strong>.</li>
              <li>Configure the OAuth consent screen (External, add your email as test user).</li>
              <li>Create OAuth 2.0 credentials (type: <em>Web application</em>). Add the redirect URI shown below.</li>
              <li>Send me the <strong>Client ID</strong> and <strong>Client Secret</strong> — I'll set them in the backend.</li>
            </ol>
            <div className="mt-4 bg-card border border-border rounded p-3 font-mono text-xs">
              <p className="label-tiny mb-1">Redirect URI to register</p>
              <code data-testid="drive-redirect-uri">{window.location.origin}/api/drive/callback</code>
            </div>
            <div className="mt-2 bg-card border border-border rounded p-3 font-mono text-xs">
              <p className="label-tiny mb-1">Folder ID we'll watch</p>
              <code>1ozH52SV9TZMZuXNvT4EBZko0r0sBD9Yr</code>
              <p className="text-muted-foreground font-sans mt-1">
                (from <a className="text-primary underline" target="_blank" rel="noopener" href="https://drive.google.com/drive/folders/1ozH52SV9TZMZuXNvT4EBZko0r0sBD9Yr">your Drive folder</a>)
              </p>
            </div>
          </div>
        ) : drive.connected ? (
          <div data-testid="drive-connected">
            <div className="flex items-center gap-2 text-[hsl(132,18%,36%)] mb-4">
              <CheckCircle2 className="w-4 h-4" />
              <span className="font-semibold">Connected and watching folder</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Schedule" value={<span className="font-mono text-xs">23:00 SAST · daily</span>} />
              <Stat label="Next sync" value={drive.next_sync ? new Date(drive.next_sync).toLocaleString("en-ZA") : "—"} />
              <Stat label="Last poll" value={drive.last_poll ? new Date(drive.last_poll).toLocaleString("en-ZA") : "—"} />
              <Stat label="Last success" value={drive.last_success ? new Date(drive.last_success).toLocaleString("en-ZA") : "—"} />
            </div>
            {drive.last_error && (
              <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4">
                <p className="font-semibold text-destructive mb-1">Last error</p>
                <p className="text-xs text-foreground/80 break-all">
                  {drive.last_error.filename ? <span className="font-mono">{drive.last_error.filename}: </span> : null}
                  {drive.last_error.error || drive.last_error.detail || JSON.stringify(drive.last_error)}
                </p>
              </div>
            )}
            {drive.poll_running && (
              <div className="bg-primary/10 border border-primary/30 rounded p-3 text-sm mb-4"
                   data-testid="drive-running">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-primary" />
                  <span>
                    Drive sync in progress
                    {drive.poll_running_started &&
                      <span className="text-muted-foreground"> · started {new Date(drive.poll_running_started).toLocaleTimeString("en-ZA")}</span>
                    }
                  </span>
                </div>
                {drive.progress_total > 0 && (
                  <div className="mt-2 ml-6 space-y-1" data-testid="drive-progress">
                    <p className="text-xs">
                      <span className="font-mono">{drive.progress_phase || "working"}</span>
                      {" · "}
                      <span>file {drive.progress_index} of {drive.progress_total}</span>
                      {drive.progress_file && (
                        <span className="text-muted-foreground"> · {drive.progress_file}</span>
                      )}
                    </p>
                    <div className="h-1.5 bg-secondary rounded overflow-hidden">
                      <div className="h-full bg-primary transition-all duration-500"
                        style={{ width: `${Math.min(100, ((drive.progress_index || 0) / Math.max(1, drive.progress_total)) * 100)}%` }} />
                    </div>
                  </div>
                )}
                {drive.progress_total === 0 && (
                  <p className="text-xs text-muted-foreground ml-6 mt-1">
                    {drive.progress_phase === "listing" ? "Asking Google Drive for the file list…" : "Each ProRoast export takes 1–5 min to download + ingest."}
                  </p>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={pollNow} disabled={busy || drive.poll_running} data-testid="drive-poll-now"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
                <RefreshCw className={`w-4 h-4 ${busy || drive.poll_running ? "animate-spin" : ""}`} />
                {drive.poll_running ? "Syncing…" : busy ? "Polling…" : "Poll now"}
              </button>
              {isOwner && (
                <button onClick={disconnect} data-testid="drive-disconnect"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-destructive/40 text-destructive rounded text-sm hover:bg-destructive/10">
                  <Power className="w-4 h-4" /> Disconnect
                </button>
              )}
            </div>
            {pollResult && <PollSummary result={pollResult} />}
          </div>
        ) : (
          <div data-testid="drive-not-connected">
            <p className="text-sm text-muted-foreground mb-4">
              Credentials are configured. Click below to authorise the app to read your Drive folder.
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Folder we'll watch: <code className="font-mono">{drive.folder_id || "(not set)"}</code>
            </p>
            {isOwner ? (
              <button onClick={connect} disabled={busy} data-testid="drive-connect"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
                <ExternalLink className="w-4 h-4" /> Connect Google Drive
              </button>
            ) : (
              <p className="text-sm text-muted-foreground">Only the Owner can connect Google Drive.</p>
            )}
          </div>
        )}
      </section>

      <ZohoSection isOwner={isOwner} />
      <PricebookSettingsSection isOwner={isOwner} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-secondary rounded p-3">
      <p className="label-tiny mb-1">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

function ZohoSection({ isOwner }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/zoho/status"); setStatus(data); }
    catch { setStatus({ configured: false, connected: false }); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/zoho/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Zoho Books?")) return;
    await api.post("/zoho/disconnect");
    setTestResult(null);
    await load();
  };

  const test = async () => {
    setBusy(true); setTestResult(null); setTestError("");
    try {
      const { data } = await api.get("/zoho/test");
      setTestResult(data);
    } catch (e) {
      setTestError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!status) return (
    <section className="bg-card border border-border rounded p-6 mb-6" data-testid="zoho-section">
      <p className="text-muted-foreground">Loading Zoho status…</p>
    </section>
  );

  return (
    <section className="bg-card border border-border rounded p-6 mb-6" data-testid="zoho-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="label-tiny">Integration</p>
          <h2 className="font-display text-2xl font-bold mt-1">Zoho Books</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Pull supplier bills into the Green Inventory inbox and post draft Manual Journals back to Books.
          </p>
        </div>
        <BookOpen className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
      </div>

      {!status.configured && (
        <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm">
          <p className="font-semibold text-destructive">Zoho client credentials not configured.</p>
          <p className="text-xs mt-1">Set ZOHO_CLIENT_ID, ZOHO_CLIENT_SECRET, ZOHO_ORGANIZATION_ID, ZOHO_REGION, ZOHO_REDIRECT_URI in backend .env.</p>
        </div>
      )}

      {status.configured && status.connected && (
        <div data-testid="zoho-connected">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Status" value={
              <span className="inline-flex items-center gap-1 text-[hsl(132,18%,36%)]">
                <CheckCircle2 className="w-4 h-4" /> Connected
              </span>
            } />
            <Stat label="Region" value={<span className="font-mono text-xs">.{status.region}</span>} />
            <Stat label="Organisation ID" value={<span className="font-mono text-xs">{status.organization_id}</span>} />
            <Stat label="Last refresh" value={status.last_refreshed ? new Date(status.last_refreshed).toLocaleString("en-ZA") : "—"} />
          </div>
          {testResult && (
            <div className="bg-secondary rounded p-3 text-sm mb-3" data-testid="zoho-test-result">
              <p className="label-tiny mb-1">Handshake result</p>
              <p className="font-semibold">{testResult.name || "(unnamed)"}</p>
              <p className="text-xs text-muted-foreground font-mono">
                Currency: {testResult.currency_code || "—"}
                {testResult.fiscal_year_start_month && ` · FY starts month ${testResult.fiscal_year_start_month}`}
              </p>
            </div>
          )}
          {testError && (
            <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-3">
              <p className="font-semibold text-destructive">Test failed</p>
              <p className="text-xs text-foreground/80 break-all">{testError}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button onClick={test} disabled={busy} data-testid="zoho-test"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
              <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Test connection
            </button>
            <ZohoSyncNow />
            {isOwner && (
              <button onClick={disconnect} data-testid="zoho-disconnect"
                className="inline-flex items-center gap-2 px-4 py-2 border border-destructive/40 text-destructive rounded text-sm hover:bg-destructive/10">
                <Power className="w-4 h-4" /> Disconnect
              </button>
            )}
          </div>
        </div>
      )}

      {status.configured && !status.connected && (
        <div data-testid="zoho-not-connected">
          <p className="text-sm text-muted-foreground mb-2">
            Credentials are configured. Click below to authorise this app to read your Zoho Books organisation.
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Region: <code className="font-mono">.{status.region}</code> · Org ID: <code className="font-mono">{status.organization_id}</code>
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Redirect URI registered with Zoho:{" "}
            <code className="font-mono break-all">{window.location.origin}/api/zoho/callback</code>
          </p>
          {isOwner ? (
            <button onClick={connect} disabled={busy} data-testid="zoho-connect"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
              <ExternalLink className="w-4 h-4" /> Connect Zoho Books
            </button>
          ) : (
            <p className="text-sm text-muted-foreground">Only the Owner can connect Zoho Books.</p>
          )}
        </div>
      )}
    </section>
  );
}

function PollSummary({ result }) {
  if (!result) return null;
  if (result.ok === false) {
    return (
      <div className="mt-4 bg-destructive/10 border border-destructive/30 rounded p-3 text-sm" data-testid="drive-poll-result">
        <p className="font-semibold text-destructive mb-1">Sync failed</p>
        <p className="text-xs text-foreground/80">{result.error || "Unknown error."}</p>
      </div>
    );
  }
  if (result.queued) {
    return (
      <div className="mt-4 bg-primary/10 border border-primary/30 rounded p-3 text-sm" data-testid="drive-poll-result">
        <p className="font-semibold text-primary mb-1">Sync queued</p>
        <p className="text-xs text-foreground/80">{result.message}</p>
      </div>
    );
  }
  const ingested = result.ingested || [];
  const errors = result.errors || [];
  const newRoasts = ingested.reduce((s, f) => s + (f.new || 0), 0);

  let headline;
  if (newRoasts > 0) headline = `${newRoasts} new roast${newRoasts === 1 ? "" : "s"} imported`;
  else if (ingested.length > 0) headline = `${ingested.length} file${ingested.length === 1 ? "" : "s"} processed · no new roasts`;
  else headline = "Up to date — no new files in Drive";

  return (
    <div className="mt-4 bg-secondary border border-border rounded p-4" data-testid="drive-poll-result">
      <div className="flex items-center gap-2 mb-2">
        <CheckCircle2 className="w-4 h-4 text-[hsl(132,18%,36%)]" />
        <p className="font-semibold text-sm">{headline}</p>
        <span className="ml-auto label-tiny">{result.files_seen ?? 0} file{result.files_seen === 1 ? "" : "s"} in folder</span>
      </div>
      {ingested.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-0.5 mt-2">
          {ingested.map((f) => (
            <li key={f.file_id} className="font-mono break-all">
              · {f.filename} → <span className="text-foreground">{f.new || 0}</span> new
            </li>
          ))}
        </ul>
      )}
      {errors.length > 0 && (
        <div className="mt-2 text-xs">
          <p className="text-destructive font-semibold mb-1">{errors.length} error{errors.length === 1 ? "" : "s"}:</p>
          <ul className="space-y-0.5">
            {errors.map((e, i) => (
              <li key={i} className="font-mono break-all text-destructive/80">· {e.filename || e.file_id}: {e.error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ZohoSyncNow() {
  // Daily Zoho sales sync runs automatically at 23:15 SAST; this button
  // lets an Owner trigger the same job on demand (e.g. after redeploying
  // or to confirm the integration works without waiting for tonight's run).
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [justFinished, setJustFinished] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/sales/sync-info"); setInfo(data); }
    catch (e) { /* no-op */ }
  };
  useEffect(() => { load(); }, []);

  // Poll while running; flash a "just finished" banner when it transitions
  // from running → idle, so users don't think the click did nothing on
  // short syncs (which can finish in <2 s if Zoho has no new data).
  useEffect(() => {
    if (!info?.running) return;
    const startSuccess = info.last_success;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/sales/sync-info");
        setInfo(data);
        if (!data.running && data.last_success && data.last_success !== startSuccess) {
          setJustFinished(true);
          setTimeout(() => setJustFinished(false), 8000);
        }
      } catch (e) { /* keep trying */ }
    }, 3000);
    return () => clearInterval(t);
  }, [info?.running, info?.last_success]);

  const trigger = async () => {
    setBusy(true); setError(""); setJustFinished(false);
    const beforeSuccess = info?.last_success;
    try {
      await api.post("/sales/sync-now");
      // Immediately reload — for tiny accounts the sync can finish before
      // the next 3-second poll, so check fast.
      setTimeout(load, 500);
      setTimeout(async () => {
        const { data } = await api.get("/sales/sync-info");
        setInfo(data);
        if (!data.running && data.last_success && data.last_success !== beforeSuccess) {
          setJustFinished(true);
          setTimeout(() => setJustFinished(false), 8000);
        }
      }, 1500);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={trigger} disabled={busy || info?.running}
        data-testid="zoho-sync-now"
        className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-sm hover:bg-secondary disabled:opacity-50">
        <RefreshCw className={`w-4 h-4 ${(busy || info?.running) ? "animate-spin" : ""}`} />
        {info?.running ? "Syncing…" : "Sync sales now"}
      </button>
      {info?.running && (
        <div className="basis-full text-xs mt-2 px-3 py-2 bg-primary/10 border border-primary/30 rounded">
          <p>Phase: <span className="font-mono">{info.phase}</span>
            {info.running_started && (
              <span className="text-muted-foreground"> · started {new Date(info.running_started).toLocaleTimeString("en-ZA")}</span>
            )}
            <span className="text-muted-foreground"> — usually finishes in 2–5 min</span>
          </p>
        </div>
      )}
      {justFinished && !info?.running && (
        <div className="basis-full text-xs mt-2 px-3 py-2 bg-[hsl(132,18%,36%)]/10 border border-[hsl(132,18%,36%)]/30 rounded" data-testid="zoho-sync-success">
          <p className="text-[hsl(132,18%,36%)] font-semibold">
            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
            Sync complete — sales data refreshed at {new Date(info.last_success).toLocaleString("en-ZA")}
          </p>
        </div>
      )}
      {info?.phase === "error" && !info?.running && (
        <div className="basis-full text-xs mt-2 px-3 py-2 bg-destructive/10 border border-destructive/30 rounded">
          <p className="text-destructive">Last sync failed: {info.last_error}</p>
        </div>
      )}
      {error && (
        <div className="basis-full text-xs mt-2 px-3 py-2 bg-destructive/10 border border-destructive/30 rounded">
          <p className="text-destructive">{error}</p>
        </div>
      )}
      {info?.last_success && !info?.running && !justFinished && !error && (
        <p className="basis-full text-xs text-muted-foreground mt-1">
          Auto-sync daily at 23:15 SAST · last success {new Date(info.last_success).toLocaleString("en-ZA")}
        </p>
      )}
    </>
  );
}


function PricebookSettingsSection({ isOwner }) {
  const [list, setList] = useState([]);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/zoho/pricebooks/all");
      setList(data.pricebooks || []);
      // If no settings exist yet, default everything to visible
      if (Object.keys(data.settings || {}).length === 0) {
        const defaults = {};
        for (const pb of (data.pricebooks || [])) {
          defaults[pb.id] = { visible: true, label: "" };
        }
        setSettings(defaults);
      } else {
        setSettings(data.settings);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshBusy(true); setError("");
    try {
      await api.get("/zoho/pricebooks?refresh=true");
      await load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setRefreshBusy(false); }
  };

  const save = async () => {
    setBusy(true); setError("");
    try {
      await api.put("/zoho/pricebooks/settings", { settings });
      setSavedAt(new Date());
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const update = (id, patch) => {
    setSettings((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || { visible: true, label: "" }), ...patch },
    }));
  };

  return (
    <section className="bg-card border border-border rounded p-6 mt-6" data-testid="pricebook-settings-section">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="label-tiny mb-1">Manufacturing Cost · Sell-price source</p>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            Zoho price lists
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Pick which Zoho price lists appear as buttons on the Manufacturing Cost page, and rename their display label if you'd like a shorter name (e.g. "Wholesale Price List" → "Wholesale").
          </p>
        </div>
        <button onClick={refresh} disabled={refreshBusy}
          data-testid="pricebook-fetch-from-zoho"
          className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded text-xs hover:bg-secondary disabled:opacity-60">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshBusy ? "animate-spin" : ""}`} />
          {refreshBusy ? "Fetching…" : "Fetch from Zoho"}
        </button>
      </div>

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-xs mb-3">{error}</div>}

      {list.length === 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center">No price lists cached yet — click <em>Fetch from Zoho</em> above.</p>
      )}

      {list.length > 0 && (
        <>
          <table className="w-full text-sm" data-testid="pricebook-settings-table">
            <thead className="bg-secondary">
              <tr>
                <th className="text-center p-3 label-tiny w-20">Visible</th>
                <th className="text-left p-3 label-tiny">Zoho name</th>
                <th className="text-left p-3 label-tiny">Display label (override)</th>
                <th className="text-right p-3 label-tiny">SKUs</th>
                <th className="text-left p-3 label-tiny">Type</th>
              </tr>
            </thead>
            <tbody>
              {list.map((pb) => {
                const s = settings[pb.id] || { visible: true, label: "" };
                return (
                  <tr key={pb.id} className="border-t border-border" data-testid={`pricebook-row-${pb.id}`}>
                    <td className="p-3 text-center">
                      <input type="checkbox" checked={!!s.visible}
                        disabled={!isOwner}
                        onChange={(e) => update(pb.id, { visible: e.target.checked })}
                        data-testid={`pricebook-visible-${pb.id}`}
                        className="accent-[hsl(20,39%,39%)] w-4 h-4 cursor-pointer" />
                    </td>
                    <td className="p-3">
                      <p className="font-medium">{pb.name}</p>
                      {pb.is_default && <span className="text-[10px] text-muted-foreground">default</span>}
                    </td>
                    <td className="p-3">
                      <input type="text" value={s.label || ""}
                        onChange={(e) => update(pb.id, { label: e.target.value })}
                        placeholder={pb.name}
                        data-testid={`pricebook-label-${pb.id}`}
                        disabled={!isOwner}
                        className="w-full px-2 py-1 border border-border rounded text-sm" />
                    </td>
                    <td className="p-3 text-right text-xs text-muted-foreground font-mono">{pb.items_count || 0}</td>
                    <td className="p-3 text-xs text-muted-foreground">{pb.pricebook_type || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isOwner && (
            <div className="flex items-center justify-end gap-3 mt-4">
              {savedAt && <span className="text-xs text-[hsl(132,18%,36%)] font-mono">Saved {savedAt.toLocaleTimeString("en-ZA")}</span>}
              <button onClick={save} disabled={busy}
                data-testid="pricebook-save"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-60">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save settings"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
